import {ChangeDetectionStrategy, Component, HostBinding, inject, OnInit, ViewChild} from '@angular/core';
import {filter, map} from 'rxjs/operators';
import {DocumentState, InternalNotificationMessageCode} from '|api/commons';
import {DocumentAuthorizedOperation} from '|api/core';
import {
  CirculationToolbarButtonsComponent,
  DocumentDetailService,
  DocumentDtoWithAuthorization,
  DocumentToolbarButtonsComponent,
  DocumentView,
  EntityCommentsViewerComponent,
  getObjectTagColor,
  isAuthorizedOperationGranted,
  load,
  ObjectDetailPart,
  TaskActivityStateIconComponent,
  UserCategoryTagComponent,
} from '|shared';
import {
  IconComponent,
  LabelComponent,
  LoadingIndicatorService,
  TagComponent,
  TooltipDirective
} from '@icz/angular-essentials';
import {AsyncPipe} from '@angular/common';
import {
  DocumentFileDetailToolbarComponent
} from '../../../document-shared/components/document-file-detail-toolbar.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslatePipe} from '@ngx-translate/core';
import {DocumentDetailRoute, WebSocketNotificationsService} from '|ui-essl-core';

@Component({
  selector: 'icz-document-detail-toolbar',
  templateUrl: './document-detail-toolbar.component.html',
  styleUrls: ['./document-detail-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TagComponent, IconComponent, TooltipDirective, TaskActivityStateIconComponent, LabelComponent, DocumentToolbarButtonsComponent, CirculationToolbarButtonsComponent, AsyncPipe, EntityCommentsViewerComponent, TranslatePipe, UserCategoryTagComponent]
})
export class DocumentDetailToolbarComponent extends DocumentFileDetailToolbarComponent<DocumentDtoWithAuthorization> implements OnInit {

  protected loadingService = inject(LoadingIndicatorService);
  protected documentDetailService = inject(DocumentDetailService);
  private wsNotificationService = inject(WebSocketNotificationsService);

  @ViewChild(EntityCommentsViewerComponent)
  entityCommentsViewerComponent!: EntityCommentsViewerComponent;

  @HostBinding('class.deactivated')
  get showToolbarAsDeactivated() {
    return this.object && [DocumentState.DEACTIVATED, DocumentState.EXPORTED, DocumentState.REGISTERED_EXTERNALLY, DocumentState.LOST].includes(this.object.documentState!);
  }

  get isReadyForAgendaTransfer() {
    return this.object?.readyForAgendaTransfer && this.object?.documentState !== DocumentState.EXPORTED;
  }

  get allowComments() {
    return [DocumentState.IN_PROGRESS, DocumentState.SUSPENDED].includes(this.object.documentState!);
  }

  get hasProfilePermission() {
    return isAuthorizedOperationGranted(this.object?.authorization, DocumentAuthorizedOperation.DOCUMENT_SHOW_PROFILE);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.wsNotificationService.getMessageListener$(InternalNotificationMessageCode.SETTLEMENT_DOCUMENT_SUCCESS)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(_ => {
        this.entityCommentsViewerComponent.loadComments();
      }
    );
  }

  parentFile$ = this.documentDetailService.parentFileForDocument$;

  canShowDocumentActivities$ = this.documentDetailService.object$.pipe(
    filter(Boolean),
    map(document => isAuthorizedOperationGranted(document.authorization, DocumentAuthorizedOperation.DOCUMENT_SHOW_ACTIVITIES)),
  );

  onCirculationActionCompleted() {
    this.documentDetailService.reloadObject({
      [ObjectDetailPart.OBJECT_DATA]: load(),
      [DocumentDetailRoute.ACTIVITIES]: load(),
    });
  }

  protected readonly DocumentView = DocumentView;
  protected readonly DocumentState = DocumentState;
  protected readonly getObjectTagColor = getObjectTagColor;
  protected readonly isAuthorizedOperationGranted = isAuthorizedOperationGranted;
  protected readonly DocumentAuthorizedOperation = DocumentAuthorizedOperation;

}
